/**
 * Cassanova API Client
 *
 * Axios-based HTTP client with automatic authentication and error handling.
 * Includes request/response interceptors for token management and retry logic.
 *
 * @module cassanova/apiClient
 */

import axios from 'axios';
import tokenManager from './tokenManager';
import {API_URL, API_VERSION, MAX_RETRY_ATTEMPTS, REQUEST_TIMEOUT} from './config';

/**
 * Error codes for categorized error handling
 */
export const ErrorCodes = {
    AUTH_FAILED: 'CASSANOVA_AUTH_FAILED',
    NETWORK_ERROR: 'CASSANOVA_NETWORK_ERROR',
    SERVER_ERROR: 'CASSANOVA_SERVER_ERROR',
    RATE_LIMIT: 'CASSANOVA_RATE_LIMIT',
    TIMEOUT: 'CASSANOVA_TIMEOUT',
    NOT_FOUND: 'CASSANOVA_NOT_FOUND',
    FORBIDDEN: 'CASSANOVA_FORBIDDEN',
    UNKNOWN: 'CASSANOVA_UNKNOWN_ERROR',
};

/**
 * Create Axios instance with base configuration
 */
const apiClient = axios.create({
    baseURL: API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'x-version': API_VERSION,
    },
});

/**
 * Request interceptor - Add authentication headers
 */
apiClient.interceptors.request.use(
    async (config) => {
        try {
            // Get valid token from token manager
            const token = await tokenManager.getToken();

            // Add authorization header
            config.headers.authorization = `Bearer ${token}`;

            if (process.env.NODE_ENV !== 'production') {
                console.log(`[API Client] ${config.method.toUpperCase()} ${config.url}`);
            }

            return config;
        } catch (error) {
            console.error('[API Client] Request interceptor error:', error);
            return Promise.reject(error);
        }
    },
    (error) => {
        console.error('[API Client] Request configuration error:', error);
        return Promise.reject(error);
    }
);

/**
 * Response interceptor - Handle errors and retry logic
 */
apiClient.interceptors.response.use(
    (response) => {
        // Success response
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[API Client] Response ${response.status}:`, response.config.url);
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Prevent infinite retry loops
        if (!originalRequest) {
            return Promise.reject(categorizeError(error));
        }

        // Initialize retry count
        if (!originalRequest._retryCount) {
            originalRequest._retryCount = 0;
        }

        // Handle 401 Unauthorized - Token expired or invalid
        if (error.response?.status === 401 && originalRequest._retryCount < MAX_RETRY_ATTEMPTS) {
            originalRequest._retryCount++;

            if (process.env.NODE_ENV !== 'production') {
                console.log(`[API Client] 401 Unauthorized, refreshing token (attempt ${originalRequest._retryCount})...`);
            }

            try {
                // Clear token and get a new one
                await tokenManager.refreshToken();

                // Retry the original request
                return apiClient(originalRequest);
            } catch (refreshError) {
                console.error('[API Client] Token refresh failed:', refreshError);
                return Promise.reject(categorizeError(refreshError));
            }
        }

        // Handle other errors
        return Promise.reject(categorizeError(error));
    }
);

/**
 * Categorize error for consistent error handling
 * @param {Error} error - Axios error object
 * @returns {Object} Categorized error object
 */
function categorizeError(error) {
    const categorized = {
        success: false,
        error: {
            code: ErrorCodes.UNKNOWN,
            message: 'An unexpected error occurred',
            details: error.message,
            retryable: false,
            originalError: error,
        },
    };

    // Network errors (no response received)
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        categorized.error.code = ErrorCodes.TIMEOUT;
        categorized.error.message = 'Request timed out. Please try again.';
        categorized.error.retryable = true;
    } else if (error.request && !error.response) {
        categorized.error.code = ErrorCodes.NETWORK_ERROR;
        categorized.error.message = 'Network error. Please check your connection.';
        categorized.error.retryable = true;
    }
    // HTTP error responses
    else if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText;

        switch (status) {
            case 401:
                categorized.error.code = ErrorCodes.AUTH_FAILED;
                categorized.error.message = 'Authentication failed. Please try again.';
                categorized.error.retryable = false;
                break;

            case 403:
                categorized.error.code = ErrorCodes.FORBIDDEN;
                categorized.error.message = 'Access forbidden. You do not have permission.';
                categorized.error.retryable = false;
                break;

            case 404:
                categorized.error.code = ErrorCodes.NOT_FOUND;
                categorized.error.message = 'Resource not found.';
                categorized.error.retryable = false;
                break;

            case 429:
                categorized.error.code = ErrorCodes.RATE_LIMIT;
                categorized.error.message = 'Too many requests. Please wait and try again.';
                categorized.error.retryable = true;
                break;

            case 500:
            case 502:
            case 503:
            case 504:
                categorized.error.code = ErrorCodes.SERVER_ERROR;
                categorized.error.message = 'Server error. Please try again later.';
                categorized.error.details = `${status} ${statusText}`;
                categorized.error.retryable = true;
                break;

            default:
                categorized.error.message = `Request failed: ${status} ${statusText}`;
                categorized.error.details = error.response.data?.message || statusText;
                categorized.error.retryable = false;
        }

        // Include response data if available
        if (error.response.data) {
            categorized.error.responseData = error.response.data;
        }
    }

    // Log error in development
    if (process.env.NODE_ENV !== 'production') {
        console.error('[API Client] Categorized error:', categorized.error);
    }

    return categorized;
}

/**
 * Helper function to check if an error is retryable
 * @param {Object} error - Categorized error object
 * @returns {boolean} True if error is retryable
 */
export function isRetryableError(error) {
    return error?.error?.retryable === true;
}

/**
 * Helper function to extract error message
 * @param {Object} error - Categorized error object
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
    return error?.error?.message || 'An unexpected error occurred';
}

/**
 * Helper function to check if error is authentication-related
 * @param {Object} error - Categorized error object
 * @returns {boolean} True if error is auth-related
 */
export function isAuthError(error) {
    return error?.error?.code === ErrorCodes.AUTH_FAILED;
}

/**
 * Helper function to check if error is network-related
 * @param {Object} error - Categorized error object
 * @returns {boolean} True if error is network-related
 */
export function isNetworkError(error) {
    return error?.error?.code === ErrorCodes.NETWORK_ERROR ||
        error?.error?.code === ErrorCodes.TIMEOUT;
}

export default apiClient;
